import { Status } from '@essent/common';
import { EnergyType } from '@essent/new-customer';
import {
  getDurationViewModel,
  getOfferCostsPerUnit,
} from '@innogy/become-a-customer/shared';
import type {
  CostPerUnitVM,
  Offer,
} from '@innogy/become-a-customer/shared/interfaces';
import { createSelector } from '@ngrx/store';

import { getAddressHasError } from '../address/address.selectors';
import { getConsumptionHasError } from '../consumption/consumption.selectors';
import { getFlowIdHasError, getFlowIdString } from '../flow/flow-id.selectors';
import { getRetentionState } from '../retention-feature.selector';

export const getGetOfferState = createSelector(
  getRetentionState,
  (state) => state.getOffer
);

export const getOfferEntry = createSelector(
  getGetOfferState,
  getFlowIdString,
  (state, flowId) =>
    flowId != null ? state.getOfferState.entities[flowId] : undefined
);

export const getUpdateOfferEntry = createSelector(
  getGetOfferState,
  getFlowIdString,
  (state, flowId) =>
    flowId != null ? state.updateOfferState.entities[flowId] : undefined
);

export const getOfferStatus = createSelector(
  getOfferEntry,
  (entry) => entry?.status
);

export const getUpdateOfferStatus = createSelector(
  getUpdateOfferEntry,
  (entry) => entry?.status
);

export const getOfferData = createSelector(
  getOfferEntry,
  (entry) => entry?.entry
);

export const getOffers = createSelector(
  getOfferData,
  (offerData) => offerData?.offers || []
);

export const getDurations = createSelector(
  getOfferData,
  (offerData) => offerData?.durations || []
);

export const getOfferHasError = createSelector(
  getOfferStatus,
  (status) => status === Status.ERROR
);

export const getUpdateOfferHasError = createSelector(
  getUpdateOfferStatus,
  getOffers,
  getFlowIdHasError,
  (status, offers, hasFlowIdError) =>
    (status === Status.SUCCESS && offers.length === 0) ||
    status === Status.ERROR ||
    hasFlowIdError
);

export const isGetOfferLoading = createSelector(
  getOfferStatus,
  getOfferHasError,
  (status, hasError) =>
    (status === undefined ||
      status === Status.PENDING ||
      status === Status.IDLE) &&
    !hasError
);

export const getSelectedDuration = createSelector(
  getGetOfferState,
  (offerState) => offerState.selectedDuration
);

export const getSelectedCampaignId = createSelector(
  getGetOfferState,
  (offerState) => offerState.selectedCampaignId
);

export const getDefaultSelectedOffer = createSelector(getOffers, (offers) =>
  offers.length > 0 ? offers[0] : undefined
);

export const getAmountOfOffers = createSelector(
  getOffers,
  (offers) => offers.length
);

export const getSelectedOffer = createSelector(
  getOffers,
  getSelectedCampaignId,
  getSelectedDuration,
  getDefaultSelectedOffer,
  (offers, selectedCampaignId, selectedDuration, defaultSelectedOffer) =>
    offers.find(
      (offer: Offer) =>
        offer.campaignId === selectedCampaignId &&
        offer.duration === selectedDuration
    ) || defaultSelectedOffer
);

export const hasSelectedInvalidOffer = createSelector(
  getOffers,
  getSelectedCampaignId,
  getSelectedDuration,
  (offers, selectedCampaignId, selectedDuration) =>
    !!selectedCampaignId &&
    !!selectedDuration &&
    offers.every(
      (offer: Offer) =>
        offer.campaignId !== selectedCampaignId ||
        offer.duration !== selectedDuration
    )
);

export const getSelectedOfferIndex = createSelector(
  getOffers,
  getSelectedOffer,
  (offers, selectedOffer) =>
    selectedOffer != null ? offers.indexOf(selectedOffer) : 1
);

export const getOfferDurationVM = createSelector(
  getOffers,
  getDurations,
  (offers, durations) =>
    offers.map((offer) => getDurationViewModel(offer, durations))
);

export const getOrderPage = createSelector(
  getGetOfferState,
  (offerState) => offerState.orderPage
);

// This is temporary implementation of fetching the details. Later,
// this data will be normalized at the API, so that it can be
// neatly retreived from the state.
export const getCostsPerUnit = createSelector(
  getSelectedOffer,
  (state): CostPerUnitVM => getOfferCostsPerUnit(state)
);

export const getElectricityPriceOverview = createSelector(
  getSelectedOffer,
  (offerState) =>
    offerState?.offerOverviews.find(
      (offerOverview) => offerOverview.energyType === EnergyType.ELECTRICITY
    )
);

export const getGasPriceOverview = createSelector(
  getSelectedOffer,
  (offerState) =>
    offerState?.offerOverviews.find(
      (offerOverview) => offerOverview.energyType === EnergyType.GAS
    )
);

export const hasOfferError = createSelector(
  getOfferHasError,
  getConsumptionHasError,
  getAddressHasError,
  getFlowIdHasError,
  getUpdateOfferHasError,
  (offerError, consumptionError, addressError, flowError, updateOfferError) =>
    offerError ||
    consumptionError ||
    addressError ||
    flowError ||
    updateOfferError
);

export const emptyOfferError = createSelector(
  getOfferData,
  isGetOfferLoading,
  (offerData, isLoading) => {
    return !isLoading && !offerData?.offers.length;
  }
);
